/**
 * Categories, locations and tags (Epic 4).
 *
 * All three are user-defined and all three are merged like anything else, which
 * means two devices can edit the same tree while offline and produce a shape
 * neither of them intended. The functions here are written for that: a parent
 * chain can be circular, a parent can be missing, and a depth limit can be
 * exceeded by a concurrent edit that neither device could have rejected on its
 * own. None of those may crash a render or silently drop an item.
 */
import { normalizeTagName } from './events.js';
import { resolveTagId } from './merge.js';
/** FR-4.3: categories nest to at most three levels. */
export const MAX_CATEGORY_DEPTH = 3;
const nameOf = (record) => {
    const value = record.fields.get('name')?.v;
    return typeof value === 'string' ? value : '';
};
const parentOf = (record) => {
    const value = record.fields.get('parentId')?.v;
    return typeof value === 'string' && value !== '' ? value : null;
};
const isLive = (record) => record.lifecycle?.present !== false;
/**
 * Builds the forest, ignoring deleted records.
 *
 * A node whose parent is missing or deleted is promoted to a root rather than
 * dropped: its items still reference it, and hiding it would make them look
 * uncategorized when they are not. A node caught in a parent cycle is likewise
 * promoted, because a cycle has no root to hang it from and two devices editing
 * the same branch offline can create one.
 */
export function buildTree(records) {
    const live = new Map();
    for (const [id, record] of records)
        if (isLive(record))
            live.set(id, record);
    /**
     * Whether walking up from a node terminates.
     *
     * A chain that reaches a missing or deleted ancestor is fine: that ancestor is
     * promoted to a root and the walk ends there. Only a loop is a problem, because
     * it never ends -- and detaching the whole subtree when a *grandparent* was
     * deleted would flatten a tree that is still perfectly well formed below the
     * break.
     */
    const acyclic = (id) => {
        const seen = new Set([id]);
        let current = parentOf(live.get(id));
        while (current !== null) {
            if (seen.has(current))
                return false;
            const parent = live.get(current);
            if (parent === undefined)
                return true;
            seen.add(current);
            current = parentOf(parent);
        }
        return true;
    };
    const nodes = new Map();
    for (const [id, record] of live) {
        const parentId = parentOf(record);
        const attached = parentId !== null && live.has(parentId) && acyclic(id);
        nodes.set(id, {
            id,
            name: nameOf(record),
            parentId: attached ? parentId : null,
            depth: 1,
            children: [],
        });
    }
    const roots = [];
    for (const node of nodes.values()) {
        const parent = node.parentId === null ? undefined : nodes.get(node.parentId);
        if (parent === undefined)
            roots.push(node);
        else
            parent.children.push(node);
    }
    // Depth is assigned by walking down, which is the only way it can be right for
    // a node whose parent was itself promoted.
    const assign = (node, depth) => ({
        ...node,
        depth,
        children: node.children
            .map((child) => assign(child, depth + 1))
            .sort((a, b) => a.name.localeCompare(b.name)),
    });
    return roots.map((root) => assign(root, 1)).sort((a, b) => a.name.localeCompare(b.name));
}
/** Depth-first, so a select can render the tree as an indented flat list. */
export function flattenTree(nodes) {
    const out = [];
    const walk = (node) => {
        out.push(node);
        for (const child of node.children)
            walk(child);
    };
    for (const node of nodes)
        walk(node);
    return out;
}
/**
 * Whether a new child may be added under a parent.
 *
 * `null` means a new root, which is always allowed. An unknown parent is refused
 * rather than treated as a root: it usually means the parent was deleted on
 * another device, and silently re-rooting a category someone meant to nest would
 * be a surprising way to resolve that.
 */
export function canNestUnder(nodes, parentId, maxDepth = MAX_CATEGORY_DEPTH) {
    if (parentId === null)
        return true;
    const parent = flattenTree(nodes).find((node) => node.id === parentId);
    return parent !== undefined && parent.depth < maxDepth;
}
/**
 * How many levels the subtree rooted at a node occupies. 1 for a leaf.
 *
 * Needed because moving a node moves everything under it, and the depth limit
 * applies to the deepest descendant rather than to the node being dragged.
 */
export function subtreeHeight(nodes, id) {
    const start = flattenTree(nodes).find((node) => node.id === id);
    if (start === undefined)
        return 0;
    const height = (node) => node.children.length === 0 ? 1 : 1 + Math.max(...node.children.map(height));
    return height(start);
}
/**
 * Whether an existing node may be moved under a prospective parent.
 *
 * Distinct from `canNestUnder`, and the difference is the bug it exists to
 * prevent: creating a category adds a leaf, so the parent's own depth is the
 * only constraint, but *moving* one carries its children along. A two-level
 * subtree moved under a depth-2 parent would put its grandchildren at depth 4,
 * past what FR-4.3 allows -- and nothing downstream would reject it, because by
 * then it is just a parent pointer like any other.
 *
 * A move under the node itself or under one of its own descendants is refused as
 * well. That is a cycle, and while `buildTree` survives one by promoting the
 * loop to a root, the subtree would appear to vanish from where it was put.
 */
export function canMoveUnder(nodes, id, parentId, maxDepth = MAX_CATEGORY_DEPTH) {
    if (parentId === id)
        return false;
    if (parentId !== null && withDescendants(nodes, id).has(parentId))
        return false;
    const height = subtreeHeight(nodes, id);
    if (height === 0)
        return false;
    if (parentId === null)
        return height <= maxDepth;
    const parent = flattenTree(nodes).find((node) => node.id === parentId);
    return parent !== undefined && parent.depth + height <= maxDepth;
}
/**
 * How many items are filed directly under each node.
 *
 * Direct rather than cumulative: the number is shown when deleting one, and what
 * matters there is how many items would be left pointing at a category that no
 * longer exists. Children are promoted rather than deleted, so their items are
 * unaffected and counting them would overstate the damage.
 */
export function taxonomyUsage(manifest, field) {
    const counts = new Map();
    for (const item of manifest.items.values()) {
        if (item.lifecycle?.present === false)
            continue;
        const id = item.fields[field]?.v;
        if (typeof id !== 'string' || id === '')
            continue;
        counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
}
/** The path from the root, for showing where something sits without a tree widget. */
export function pathTo(nodes, id) {
    const path = [];
    const walk = (node, ancestors) => {
        const here = [...ancestors, node.name];
        if (node.id === id) {
            path.push(...here);
            return true;
        }
        return node.children.some((child) => walk(child, here));
    };
    for (const node of nodes)
        if (walk(node, []))
            break;
    return path;
}
/**
 * A label for each category, by id, for ordering items by where they are filed.
 *
 * The full path rather than the leaf name: "Clothing / Tops" sorts beside
 * "Clothing" and before "Footwear", so the things in a category stay together
 * and a sub-category sits under its parent rather than wherever its own initial
 * happens to fall. Sorting by leaf name alone would scatter Tops and Trainers
 * into the Ts and separate both from the category they belong to.
 *
 * Built once for the whole manifest, because resolving a path per comparison
 * would walk the tree O(n log n) times.
 */
export function categoryPaths(manifest) {
    const nodes = buildTree(manifest.categories);
    const paths = new Map();
    const walk = (node, ancestors) => {
        const here = [...ancestors, node.name];
        paths.set(node.id, here.join(' / '));
        for (const child of node.children)
            walk(child, here);
    };
    for (const node of nodes)
        walk(node, []);
    return paths;
}
/**
 * Every descendant of a node, including itself.
 *
 * Filtering by a category should include the things filed beneath it: someone
 * asking for "Clothing" means the shirts in "Clothing / Tops" too.
 */
export function withDescendants(nodes, id) {
    const found = new Set();
    const collect = (node) => {
        found.add(node.id);
        for (const child of node.children)
            collect(child);
    };
    const start = flattenTree(nodes).find((node) => node.id === id);
    if (start !== undefined)
        collect(start);
    return found;
}
/**
 * A node and every ancestor above it, nearest first.
 *
 * Cycle-guarded, because two devices editing the same branch offline can produce
 * one and an unguarded walk would not terminate. A chain that reaches a missing
 * or deleted ancestor simply stops there.
 */
export function ancestorsOf(records, id) {
    const chain = [];
    const seen = new Set();
    let current = id;
    while (current !== null && !seen.has(current)) {
        const record = records.get(current);
        if (record === undefined || !isLive(record))
            break;
        seen.add(current);
        chain.push(current);
        current = parentOf(record);
    }
    return chain;
}
// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------
/**
 * Tags still in use, with merged-away tags folded into their survivor.
 *
 * A merged tag is not shown as its own entry: it has no separate identity to a
 * reader any more, even though its id lives on in the events that applied it.
 */
export function activeTags(manifest) {
    const live = [];
    for (const [tagId, tag] of manifest.tags) {
        if (tag.lifecycle?.present === false)
            continue;
        if (resolveTagId(manifest, tagId) !== tagId)
            continue;
        if (tag.displayName?.v === undefined)
            continue;
        live.push(tag);
    }
    return live.sort((a, b) => (a.displayName?.v ?? '').localeCompare(b.displayName?.v ?? ''));
}
/**
 * An existing tag with the same name, if there is one.
 *
 * FR-4.14 compares case- and whitespace-insensitively while preserving the
 * casing someone typed, so this is the check that stops "Camping" and "camping"
 * becoming two tags that then split every count between them.
 */
export function findTagByName(manifest, displayName) {
    const wanted = normalizeTagName(displayName);
    if (wanted === '')
        return undefined;
    return activeTags(manifest).find((tag) => normalizeTagName(tag.displayName?.v ?? '') === wanted);
}
/** How many items currently carry each tag, for showing which are worth keeping. */
export function tagUsage(manifest) {
    const counts = new Map();
    for (const item of manifest.items.values()) {
        for (const [tagId, applied] of item.tags) {
            if (!applied.present)
                continue;
            const resolved = resolveTagId(manifest, tagId);
            counts.set(resolved, (counts.get(resolved) ?? 0) + 1);
        }
    }
    return counts;
}
//# sourceMappingURL=taxonomy.js.map